import {Box} from "@mui/material";
import {ManageAccountsOutlined as ManagerIcon} from "@mui/icons-material";
import {useCallback, useEffect, useState} from "react";
import AddOwnerDialog, {AvailabilityCheck, OwnerSubmitPayload} from "../company/AddOwnerDialog";
import ManagerCapabilitiesPicker from "./ManagerCapabilitiesPicker";
import ManagedTrainersPicker from "./ManagedTrainersPicker";
import {checkManagerAvailability, createManager} from "../../services/ManagerService";
import {getUsersWithoutRoles} from "../../services/UserService";
import {promotableCandidates} from "../../utils/userCandidates";
import type {UserCandidate} from "../../utils/userCandidates";
import {ROLES} from "../../enums/Roles";
import {useMessageContext} from "../../contexts/MessageContext";
import {CAPABILITIES} from "../../enums/Capabilities";
import type {Capability} from "../../enums/Capabilities";
import {colors, borders} from "../ui/colors";

export type AddManagerDialogProps = {
    open: boolean;
    close: () => void;
    setReload?: (updater: (prev: boolean) => boolean) => void;
};

/**
 * Add-a-Manager drawer. Reuses the shared Add-Owner flow (name, email, phone, password + confirm) and
 * injects the two things that define this persona: the capability catalogue and the trainer scope.
 *
 * <p>Unlike the group-admin dialog this one keeps `allowExistingUser`, because promoting an existing
 * company user is a first-class flow — the server takes either a `userId` or the profile fields.
 *
 * <p><b>`availableUsers` is passed deliberately.</b> Left out, AddOwnerDialog falls back to its own
 * candidate fetch, which calls a SYSTEM_ADMIN-only endpoint and would 403 for the company admins this
 * screen is built for. We source the pool from `/admin/user` instead, which is company-scoped from the
 * principal.
 */
const AddManagerDialog = ({open, close, setReload}: AddManagerDialogProps) => {
    const notify = useMessageContext()?.notificationHandler;

    const [capabilities, setCapabilities] = useState<Capability[]>([]);
    const [managesAll, setManagesAll] = useState<boolean>(true);
    const [trainerIds, setTrainerIds] = useState<number[]>([]);
    const [availableUsers, setAvailableUsers] = useState<UserCandidate[]>([]);

    useEffect(() => {
        if (!open) return;
        setCapabilities([]);
        setManagesAll(true);
        setTrainerIds([]);
        // Candidates for the promote path: neither already an ADMIN nor already a MANAGER. Both are
        // asked of the server, which is where the pool is decided — createManager refuses either one,
        // so a picker offering them would be offering a choice that cannot succeed.
        //
        // promotableCandidates re-applies the same rule client-side. Not redundant: it also drops
        // disabled users, and it covers the case where this list is handed to AddOwnerDialog as a
        // prop, where nothing guarantees it came from the filtered endpoint.
        getUsersWithoutRoles([ROLES.ADMIN, ROLES.MANAGER])
            .then((res: any) => setAvailableUsers(
                promotableCandidates(res?.data, [ROLES.ADMIN, ROLES.MANAGER]),
            ))
            .catch(() => setAvailableUsers([]));
    }, [open]);

    const handleClose = () => {
        setCapabilities([]);
        setManagesAll(true);
        setTrainerIds([]);
        close();
    };

    // Stable identity so AddOwnerDialog's blur handler isn't re-created on every render.
    const checkAvailability = useCallback<AvailabilityCheck>(
        ({email, phoneNumber}) => checkManagerAvailability({email, phoneNumber}),
        [],
    );

    const handleSubmit = (payload: OwnerSubmitPayload) => {
        createManager({
            // Present on the promote path; the server then ignores the profile fields entirely.
            userId: payload.userId ? Number(payload.userId) : undefined,
            name: payload.name,
            email: payload.email,
            phoneNumber: payload.phoneNumber,
            password: payload.password ?? undefined,
            capabilities,
            managesAllTrainers: managesAll,
            trainerIds: managesAll ? [] : trainerIds,
        })
            .then(() => notify?.success("המנהל נוצר בהצלחה"))
            .then(() => setReload?.((prev: boolean) => !prev))
            .then(handleClose)
            .catch(() => notify?.error("אירעה שגיאה ביצירת המנהל"));
    };

    // The trainer scope only means something to someone who manages trainers: without that
    // permission the server never consults the scope at all, so asking for it would be collecting an
    // answer that changes nothing. Mirrors ManagerPermissionsPage.
    const managesTrainers = capabilities.includes(CAPABILITIES.MANAGE_TRAINERS);

    const permissionsSection = (
        <Box sx={{marginTop: "20px", paddingTop: "20px", borderTop: borders.default}}>
            <ManagerCapabilitiesPicker value={capabilities} onChange={setCapabilities}/>
            {managesTrainers && (
                <Box sx={{marginTop: "16px"}}>
                    <ManagedTrainersPicker
                        active={open && managesTrainers}
                        managesAll={managesAll}
                        onManagesAllChange={setManagesAll}
                        selectedTrainerIds={trainerIds}
                        onSelectedTrainerIdsChange={setTrainerIds}
                    />
                </Box>
            )}
        </Box>
    );

    return (
        <AddOwnerDialog
            open={open}
            close={handleClose}
            companyId={null}
            handleSubmit={handleSubmit}
            title="מנהל חדש"
            titleIcon={<ManagerIcon sx={{color: colors.textSecondary, fontSize: "20px"}}/>}
            availableUsers={availableUsers}
            checkAvailability={checkAvailability}
        >
            {permissionsSection}
        </AddOwnerDialog>
    );
};

export default AddManagerDialog;
